import { parseISO } from "date-fns";
import type { CollectionJob } from "@/services/api/collection-jobs";

export type CanonicalStatus =
  | "Completed"
  | "Running"
  | "Pending"
  | "Failed"
  | "Cancelled";

export const CANONICAL_STATUS_CONFIG: Record<
  CanonicalStatus,
  { variant: "default" | "outline" | "secondary"; color: string }
> = {
  Completed: { variant: "default", color: "bg-emerald-500" },
  Running: { variant: "default", color: "bg-blue-500" },
  Pending: { variant: "secondary", color: "bg-amber-500" },
  Failed: { variant: "outline", color: "bg-red-500" },
  Cancelled: { variant: "outline", color: "bg-grid-400" },
} as const;

export function normalizeStatusDescription(
  statusDescription: string
): CanonicalStatus {
  if (!statusDescription) return "Pending";

  const normalized = statusDescription.trim().toLowerCase();

  if (
    normalized.includes("completed") ||
    normalized.includes("success") ||
    normalized.includes("finished") ||
    normalized.includes("done")
  ) {
    return "Completed";
  }

  if (
    normalized.includes("running") ||
    normalized.includes("in progress") ||
    normalized.includes("processing") ||
    normalized.includes("active")
  ) {
    return "Running";
  }

  if (
    normalized.includes("failed") ||
    normalized.includes("error") ||
    normalized.includes("timeout") ||
    normalized.includes("insufficient") ||
    normalized.includes("credit")
  ) {
    return "Failed";
  }

  if (normalized.includes("cancel")) {
    return "Cancelled";
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("queued") ||
    normalized.includes("waiting")
  ) {
    return "Pending";
  }

  return "Pending";
}

export function getStatusBadgeVariant(
  statusDescription: string
): "default" | "outline" | "secondary" {
  const canonical = normalizeStatusDescription(statusDescription);
  return CANONICAL_STATUS_CONFIG[canonical]?.variant ?? "outline";
}

export function getStatusColor(statusDescription: string): string {
  const canonical = normalizeStatusDescription(statusDescription);
  return CANONICAL_STATUS_CONFIG[canonical]?.color ?? "bg-grid-300";
}

export function getCanonicalStatusName(
  statusDescription: string
): CanonicalStatus {
  return normalizeStatusDescription(statusDescription);
}

export function calculateProgressPercentage(
  processed: number,
  total: number,
  statusDescription?: string
): number {
  // If job is completed, show 100% regardless of numbers
  if (statusDescription) {
    const canonical = normalizeStatusDescription(statusDescription);
    if (canonical === "Completed") {
      return 100;
    }
  }
  if (total === 0) {
    return 0;
  }

  const percentage = Math.round((processed / total) * 100);
  return Math.max(0, Math.min(100, percentage));
}

export function canCancel(status: CollectionJob["status"]): boolean {
  if (typeof status !== "number") {
    return false;
  }

  const statusNumber = status;
  const terminalStatusNumbers = [2, 3, 4];

  return !terminalStatusNumbers.includes(statusNumber);
}

export function shortenStatusDescription(
  statusDescription: string,
  maxLength: number = 20
): string {
  if (!statusDescription) return "";
  const canonical = getCanonicalStatusName(statusDescription);
  if (canonical.length <= maxLength) return canonical;
  return canonical.substring(0, maxLength) + "...";
}

export function sortCollectionJobs(
  jobs: CollectionJob[],
  sortColumn: string,
  sortDirection: "asc" | "desc"
): CollectionJob[] {
  const sorted = [...jobs].sort((a, b) => {
    let comparison = 0;

    switch (sortColumn) {
      case "status":
        comparison = a.statusDescription.localeCompare(b.statusDescription);
        break;
      case "created":
        const createdA = a.createdDate ? parseISO(a.createdDate).getTime() : 0;
        const createdB = b.createdDate ? parseISO(b.createdDate).getTime() : 0;
        comparison = createdA - createdB;
        break;
      case "triggeredBy":
        const triggeredByA = a.triggeredBy || "";
        const triggeredByB = b.triggeredBy || "";
        comparison = triggeredByA.localeCompare(triggeredByB);
        break;
      case "collected":
        comparison = a.processedProfiles - b.processedProfiles;
        break;
      case "classified":
        comparison = a.classifiedProfiles - b.classifiedProfiles;
        break;
      case "analyzed":
        comparison = a.analyzedProfiles - b.analyzedProfiles;
        break;
      case "started":
        const startedA = a.startedAt ? parseISO(a.startedAt).getTime() : 0;
        const startedB = b.startedAt ? parseISO(b.startedAt).getTime() : 0;
        comparison = startedA - startedB;
        break;
      case "completed":
        const completedA = a.completedAt
          ? parseISO(a.completedAt).getTime()
          : 0;
        const completedB = b.completedAt
          ? parseISO(b.completedAt).getTime()
          : 0;
        comparison = completedA - completedB;
        break;
      case "remainingSearchCredits":
        const searchCreditsA = a.remainingSearchCredits ?? 0;
        const searchCreditsB = b.remainingSearchCredits ?? 0;
        comparison = searchCreditsA - searchCreditsB;
        break;
      case "remainingCollectCredits":
        const collectCreditsA = a.remainingCollectCredits ?? 0;
        const collectCreditsB = b.remainingCollectCredits ?? 0;
        comparison = collectCreditsA - collectCreditsB;
        break;
      default:
        return 0;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  return sorted;
}
